// First: a page that may not use Web Storage gets an in-memory stand-in before any module reads it.
import './utils/storageFallback';
// Then: a lazy chunk that fails to load is asked for once more before it counts as a stale build.
import './utils/chunkRetry';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';

import * as Sentry from '@sentry/react';
import { Provider } from 'react-redux';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes
} from 'react-router-dom';

import './styles/variables.css';
import './styles/components.css';
import './styles/layout.css';

import { AppDialogHost } from './components/AppDialogHost';
import { AppErrorFallback } from './components/AppErrorFallback';
import { ArtifactShareHost } from './components/ArtifactShareModal';
import { CampaignCouponHost } from './components/CampaignCouponHost';
import { EventTrackerProvider } from './components/EventTrackerProvider';
import { GlobalQuotaNotice } from './components/GlobalQuotaNotice';
import { GmailInviteReturn } from './components/GmailInviteReturn';
import { PageNavigationScrollReset } from './components/PageNavigationScrollReset';
import {
  hasInitialProjectBootstrap,
  RouteLoadingFallback,
  shouldSkipPlanningSkeleton,
} from './components/PlanningRouteShell';
import { ProtectedRoute } from './components/ProtectedRoute';
import { SignupCouponCapture } from './components/SignupCouponCapture';
import { SignupCreditAwardBridge } from './components/SignupCreditAwardBridge';
import { SupportNotificationBridge } from './components/Support/SupportNotificationBridge';
import { ToastProvider } from './components/Toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AuthModalProvider } from './context/AuthModalContext';
import { usePageTracking } from './hooks/usePageTracking';
import Landing from './pages/landing';
import { store } from './store';
import { captureMagSrvToken } from './utils/analytics';
import { installDomMutationGuard } from './utils/domMutationGuard';
import { SENTRY_NOISE_FILTERS } from './utils/sentryNoiseFilters';
import { isStaleBundleError, recoverFromStaleBundle } from './utils/staleBundle';
import { captureVisit } from './utils/visitorTracker';
import { isSupportStaff } from './utils/systemRoles';

// A tab still on a superseded build fails its next lazy import. Reloading gives
// it the current one, so report the failure only once a reload has been spent
// and the chunk is still missing. Sentry is off outside production, so the error
// boundary repeats this; the reload marker makes the pair idempotent.
const handleBoundaryError = (error: unknown): void => {
  if (isStaleBundleError(error)) recoverFromStaleBundle();
};

Sentry.init({
  // Development already has the React error overlay and should never emit
  // production telemetry. This also keeps localhost network traces focused
  // on product requests.
  enabled: process.env.NODE_ENV === 'production',
  dsn: "https://37802e61ef4bfc6c4a616c4b8daaec7d@o4511466654728192.ingest.us.sentry.io/4511471718170624",
  environment: process.env.NODE_ENV,
  tracesSampleRate: 0.1,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  // Errors are useful in production; user-identifying defaults are not needed.
  sendDefaultPii: false,
  ...SENTRY_NOISE_FILTERS,
  beforeSend: (event, hint) => (
    isStaleBundleError(hint?.originalException) && recoverFromStaleBundle() ? null : event
  )
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const PlanningPrebootCleanup: React.FC = () => {
  React.useLayoutEffect(() => {
    document.documentElement.classList.remove('planning-route-preboot');
  }, []);

  return null;
};

installDomMutationGuard();
captureMagSrvToken();
const trackingHostname = window.location.hostname.toLowerCase();
const trackingPathname = window.location.pathname;
const isProductionTrackingHost = trackingHostname === '8080.ai' || trackingHostname === 'www.8080.ai';
const isPlanningPath = trackingPathname === '/planning' || trackingPathname.startsWith('/planning/');
const isAuthCallbackPath = trackingPathname.startsWith('/auth/callback/')
  || trackingPathname === '/github/repo-auth/callback';

if (isProductionTrackingHost && !isPlanningPath && !isAuthCallbackPath) captureVisit();

// -- Lazy-loaded pages (route-based code splitting) --
const AboutPage = React.lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const PricingPage = React.lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = React.lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const PlanningPage = React.lazy(() => import('./pages/PlanningPage').then(m => ({ default: m.PlanningPage })));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const ProjectsPage = React.lazy(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const BillingPage = React.lazy(() => import('./pages/BillingPage').then(m => ({ default: m.BillingPage })));
const ChatPage = React.lazy(() => import('./pages/chat/ChatPage'));
const LoopsPage = React.lazy(() => import('./pages/loops/LoopsPage'));
const ModelsPage = React.lazy(() => import('./pages/ModelsPage'));
const TeamPage = React.lazy(() => import('./pages/TeamPage').then(m => ({ default: m.TeamPage })));
const TeamListPage = React.lazy(() => import('./pages/TeamListPage').then(m => ({ default: m.TeamListPage })));
const TeamAnalyticsPage = React.lazy(() => import('./pages/TeamAnalyticsPage').then(m => ({ default: m.TeamAnalyticsPage })));
const GrowthFeedbackPage = React.lazy(() => import('./pages/GrowthFeedbackPage').then(m => ({ default: m.GrowthFeedbackPage })));
const OrganizationManagementPage = React.lazy(() => import('./pages/OrganizationManagementPage').then(m => ({ default: m.OrganizationManagementPage })));
const OrganizationInvitationAcceptPage = React.lazy(() => import('./pages/OrganizationInvitationAcceptPage').then(m => ({ default: m.OrganizationInvitationAcceptPage })));
const InvitationAcceptPage = React.lazy(() => import('./pages/InvitationAcceptPage').then(m => ({ default: m.InvitationAcceptPage })));
const AdminSettingsPage = React.lazy(() => import('./pages/AdminSettingsPage').then(m => ({ default: m.AdminSettingsPage })));
const AdminProjectsPage = React.lazy(() => import('./pages/admin/AdminProjectsPage').then(m => ({ default: m.AdminProjectsPage })));
const AdminUsersPage = React.lazy(() => import('./pages/admin/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminMonthlyInvoicesPage = React.lazy(() => import('./pages/admin/AdminMonthlyInvoicesPage').then(m => ({ default: m.AdminMonthlyInvoicesPage })));
const AdminCouponsPage = React.lazy(() => import('./pages/admin/AdminCouponsPage').then(m => ({ default: m.AdminCouponsPage })));
const AdminCashoutsPage = React.lazy(() => import('./pages/admin/AdminCashoutsPage').then(m => ({ default: m.AdminCashoutsPage })));
const IntentAnalyticsPage = React.lazy(() => import('./pages/admin/IntentAnalyticsPage').then(m => ({ default: m.IntentAnalyticsPage })));
const QualityAnalyticsPage = React.lazy(() => import('./pages/admin/QualityAnalyticsPage').then(m => ({ default: m.QualityAnalyticsPage })));
const PerformanceAnalyticsPage = React.lazy(() => import('./pages/admin/PerformanceAnalyticsPage').then(m => ({ default: m.PerformanceAnalyticsPage })));
const AgentManagerPage = React.lazy(() => import('./pages/admin/AgentManagerPage').then(m => ({ default: m.AgentManagerPage })));
const TrafficAnalysisPage = React.lazy(() => import('./pages/admin/TrafficAnalysisPage').then(m => ({ default: m.TrafficAnalysisPage })));
const GmailInvitesPage = React.lazy(() => import('./pages/admin/GmailInvitesPage').then(m => ({ default: m.GmailInvitesPage })));
const ViralityPage = React.lazy(() => import('./pages/admin/ViralityPage').then(m => ({ default: m.ViralityPage })));
const AdminModelsPage = React.lazy(() => import('./pages/admin/AdminModelsPage').then(m => ({ default: m.AdminModelsPage })));
const TimeoutControlPage = React.lazy(() => import('./pages/admin/TimeoutControlPage').then(m => ({ default: m.TimeoutControlPage })));
const TokenManagerPage = React.lazy(() => import('./pages/admin/TokenManagerPage').then(m => ({ default: m.TokenManagerPage })));
const ModelLogsPage = React.lazy(() => import('./pages/admin/ModelLogsPage').then(m => ({ default: m.ModelLogsPage })));
const LoopLogsPage = React.lazy(() => import('./pages/admin/LoopLogsPage').then(m => ({ default: m.LoopLogsPage })));
const ArtifactLogsPage = React.lazy(() => import('./pages/admin/ArtifactLogsPage').then(m => ({ default: m.ArtifactLogsPage })));
const ProposalLogsPage = React.lazy(() => import('./pages/admin/ProposalLogsPage').then(m => ({ default: m.ProposalLogsPage })));
const WorkspaceTaskLogsPage = React.lazy(() => import('./pages/admin/ExecutionLogsPage').then(m => ({ default: m.WorkspaceTaskLogsPage })));
const ChatLogsPage = React.lazy(() => import('./pages/admin/ExecutionLogsPage').then(m => ({ default: m.ChatLogsPage })));
const GenerationLogsPage = React.lazy(() => import('./pages/admin/GenerationLogsPage').then(m => ({ default: m.GenerationLogsPage })));
const PageQualityReviewLogsPage = React.lazy(() => import('./pages/admin/PageQualityReviewLogsPage').then(m => ({ default: m.PageQualityReviewLogsPage })));
const ModelAnalyticsPage = React.lazy(() => import('./pages/admin/ModelAnalyticsPage').then(m => ({ default: m.ModelAnalyticsPage })));
const CallbackGoogle = React.lazy(() => import('./pages/auth/CallbackGoogle').then(m => ({ default: m.CallbackGoogle })));
const CallbackApple = React.lazy(() => import('./pages/auth/CallbackApple').then(m => ({ default: m.CallbackApple })));
const CallbackMicrosoft = React.lazy(() => import('./pages/auth/CallbackMicrosoft').then(m => ({ default: m.CallbackMicrosoft })));
const CallbackGitHub = React.lazy(() => import('./pages/auth/CallbackGitHub').then(m => ({ default: m.CallbackGitHub })));
const CallbackGitHubRepoConnect = React.lazy(() => import('./pages/auth/CallbackGitHubRepoConnect').then(m => ({ default: m.CallbackGitHubRepoConnect })));
const VscodeAuthPage = React.lazy(() => import('./pages/auth/VscodeAuthPage').then(m => ({ default: m.VscodeAuthPage })));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = React.lazy(() => import('./pages/auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const SharedDesignPage = React.lazy(() => import('./pages/SharedDesignPage'));
const SharedArtifactPage = React.lazy(() => import('./pages/SharedArtifactPage'));
const PublicProjectPage = React.lazy(() => import('./pages/PublicProjectPage'));
const ExplorePage = React.lazy(() => import('./pages/ExplorePage'));
const SkillStorePage = React.lazy(() => import('./pages/SkillStorePage'));
const SkillRunnerPage = React.lazy(() => import('./pages/SkillRunnerPage'));
const ReferralCapture = React.lazy(() => import('./components/ReferralCapture').then(m => ({ default: m.ReferralCapture })));
const ClaimSharePage = React.lazy(() => import('./pages/ClaimSharePage').then(m => ({ default: m.ClaimSharePage })));
const SupportPage = React.lazy(() => import('./pages/SupportPage'));
const SupportDashboardPage = React.lazy(() => import('./pages/SupportDashboardPage'));
const APIPlatformPage = React.lazy(() => import('./pages/APIPlatformPage'));
const ProofPage = React.lazy(() => import('./pages/ProofPage'));

const PageTracker: React.FC = () => { usePageTracking();

  return null; };

const AppRouteLoadingFallback: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  return (
    <RouteLoadingFallback
      pathname={location.pathname}
      skipPlanningSkeleton={shouldSkipPlanningSkeleton(
        navigationType,
        hasInitialProjectBootstrap(location.state),
      )}
    />
  );
};

const SupportRouter: React.FC = () => {
  const { user } = useAuth();
  const isStaff = isSupportStaff(user?.system_role);
  return isStaff ? <SupportDashboardPage /> : <SupportPage />;
};

const rootEl = document.getElementById('root') as HTMLElement;



const app = (
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<AppErrorFallback />} onError={handleBoundaryError}>
      <Provider store={store}>
        <Router>
        <AppDialogHost />
        <PlanningPrebootCleanup />
        <PageTracker />
        <SignupCouponCapture />
        <ToastProvider>
          <AuthProvider>
            <EventTrackerProvider />
            <SignupCreditAwardBridge />
            <SupportNotificationBridge />
            <AuthModalProvider>
              <CampaignCouponHost />
              <ArtifactShareHost />
              <GlobalQuotaNotice />
              <GmailInviteReturn />
              <Suspense fallback={<AppRouteLoadingFallback />}>
                <SentryRoutes>
                  <Route path="/" element={<Landing />} />
                    {/* real auth URLs: prerendered for no-JS; with JS the Navbar opens the modal in place */}
                    <Route path="/login" element={<Landing />} />
                    <Route path="/signup" element={<Landing />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/models" element={<ModelsPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/proof" element={<><PageNavigationScrollReset /><ProofPage /></>} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/auth/callback/google" element={<CallbackGoogle />} />
                  <Route path="/auth/callback/apple" element={<CallbackApple />} />
                  <Route path="/auth/callback/microsoft" element={<CallbackMicrosoft />} />
                  <Route path="/auth/callback/github" element={<CallbackGitHub />} />
                  <Route path="/github/repo-auth/callback" element={<CallbackGitHubRepoConnect />} />
                  <Route path="/vscode-auth" element={<VscodeAuthPage />} />
                  <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/refer/:referralCode" element={<ReferralCapture />} />
                    <Route path="/claim-share/:token" element={<ClaimSharePage />} />
                  <Route path="/loops/:loopId?" element={<ProtectedRoute><LoopsPage /></ProtectedRoute>} />
                  <Route
                    path="/chat/:conversationId?"
                    element={
                      <ProtectedRoute>
                        <ChatPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/planning"
                    element={
                      <ProtectedRoute>
                        <PlanningPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/planning/:projectId/*"
                    element={
                      <ProtectedRoute>
                        <PlanningPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects/:projectId/team"
                    element={
                      <ProtectedRoute>
                        <TeamPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/invite/:token" element={<InvitationAcceptPage />} />
                  <Route path="/organization-invite/:token" element={<OrganizationInvitationAcceptPage />} />
                  <Route
                    path="/team"
                    element={
                      <ProtectedRoute>
                        <TeamListPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team/analytics"
                    element={
                      <ProtectedRoute>
                        <TeamAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/growth-feedback"
                    element={
                      <ProtectedRoute>
                        <GrowthFeedbackPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organizations"
                    element={
                      <ProtectedRoute>
                        <OrganizationManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/settings"
                    element={
                      <ProtectedRoute>
                        <AdminSettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/projects"
                    element={
                      <ProtectedRoute>
                        <AdminProjectsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute>
                        <AdminUsersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/coupons"
                    element={
                      <ProtectedRoute>
                        <AdminCouponsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/monthly-invoices"
                    element={
                      <ProtectedRoute>
                        <AdminMonthlyInvoicesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/cashouts"
                    element={
                      <ProtectedRoute>
                        <AdminCashoutsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/traffic-analysis"
                    element={
                      <ProtectedRoute>
                        <TrafficAnalysisPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/admin/gmail-invites" element={<ProtectedRoute><GmailInvitesPage /></ProtectedRoute>} />
                  <Route path="/admin/virality" element={<ProtectedRoute><ViralityPage /></ProtectedRoute>} />
                  <Route
                    path="/admin/intent-analytics"
                    element={
                      <ProtectedRoute>
                        <IntentAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/agent-manager"
                    element={
                      <ProtectedRoute>
                        <AgentManagerPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/quality-analytics"
                    element={
                      <ProtectedRoute>
                        <QualityAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/performance-analytics"
                    element={
                      <ProtectedRoute>
                        <PerformanceAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/models"
                    element={
                      <ProtectedRoute>
                        <AdminModelsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/admin/timeouts" element={<ProtectedRoute><TimeoutControlPage /></ProtectedRoute>} />
                  <Route path="/admin/token-manager" element={<ProtectedRoute><TokenManagerPage /></ProtectedRoute>} />
                  <Route
                    path="/admin/model-logs"
                    element={
                      <ProtectedRoute>
                        <ModelLogsPage />
                      </ProtectedRoute>
                    }
                  />
                    <Route path="/admin/loop-logs" element={<ProtectedRoute><LoopLogsPage /></ProtectedRoute>} />
                    <Route path="/admin/artifact-logs" element={<ProtectedRoute><ArtifactLogsPage /></ProtectedRoute>} />
                    <Route path="/admin/proposal-logs" element={<ProtectedRoute><ProposalLogsPage /></ProtectedRoute>} />
                    <Route path="/admin/workspace-task-logs" element={<ProtectedRoute><WorkspaceTaskLogsPage /></ProtectedRoute>} />
                    <Route path="/admin/chat-logs" element={<ProtectedRoute><ChatLogsPage /></ProtectedRoute>} />
                  <Route
                    path="/admin/model-analytics"
                    element={
                      <ProtectedRoute>
                        <ModelAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/page-generation-logs"
                    element={<ProtectedRoute><GenerationLogsPage key="page" kind="page" /></ProtectedRoute>}
                  />
                  <Route
                    path="/admin/section-generation-logs"
                    element={<ProtectedRoute><GenerationLogsPage key="section" kind="section" /></ProtectedRoute>}
                  />
                    <Route path="/admin/page-quality-review-logs" element={<ProtectedRoute><PageQualityReviewLogsPage /></ProtectedRoute>} />
                  <Route
                    path="/billing"
                    element={
                      <ProtectedRoute>
                        <BillingPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/api-platform"
                    element={
                      <ProtectedRoute>
                        <APIPlatformPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects"
                    element={
                      <ProtectedRoute>
                        <ProjectsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/design/:projectId/:shareId" element={<SharedDesignPage />} />
                  <Route path="/shared/artifacts/:shareId" element={<SharedArtifactPage />} />
                  <Route path="/projects/:projectId/*" element={<PublicProjectPage />} />
                  <Route path="/explore" element={<ExplorePage />} />
                  <Route path="/skill-store" element={<SkillStorePage />} />
                  <Route path="/skill-store/:skillId" element={<SkillRunnerPage />} />
                  <Route
                    path="/support"
                    element={
                      <ProtectedRoute>
                        <SupportRouter />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/support/tickets/:ticketId"
                    element={
                      <ProtectedRoute>
                        <SupportRouter />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </SentryRoutes>
              </Suspense>
            </AuthModalProvider>
          </AuthProvider>
        </ToastProvider>
      </Router>
      </Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// The planning hard-reload shell is authored in public/index.html so it can
// paint before the JavaScript bundle. It is not a react-snap snapshot; the
// first React commit replaces it with the same geometry-aware component.
const hasPlanningPrebootShell = rootEl.firstElementChild?.id === 'planning-route-preboot';
// Only an element counts as a snapshot: outside /planning the inline body
// script removes the preboot shell and leaves whitespace text nodes behind,
// which must not trigger hydration (it would mismatch and re-render anyway).
const hasSnapshot = rootEl.firstElementChild !== null && !hasPlanningPrebootShell;

if (hasSnapshot) {
  ReactDOM.hydrateRoot(rootEl, app, {
    onRecoverableError: (error: any) => {
      // Suppress expected hydration mismatch warnings between stale snapshots and live data
      const msg = error?.message || '';
      if (
        msg.includes('Hydration failed') ||
        msg.includes('There was an error while hydrating') ||
        msg.includes('Text content did not match') ||
        msg.includes('did not match')
      ) {
        return;
      }
      console.error(error);
    }
  });
} else {
  ReactDOM.createRoot(rootEl).render(app);
}
